/**
 * Halloween – "Midnight Apothecary"
 * ------------------------------------------------------------------------------------------------
 * Seasonal effects layer: self-spinning cobwebs, a spider on a spring thread, ghosts that flee the
 * cursor, peeking ghosts on product cards, a ghost flying into the cart, a clickable pumpkin patch
 * in the footer, fog, wax drips under the announcement bar, a bat swarm and a "lights out" easter egg.
 *
 * Everything is configured from #hw-root (rendered by snippets/halloween.liquid):
 *   data-effects    comma separated list of enabled effects
 *   data-intensity  subtle | normal | full
 *   data-egg-code   optional discount code revealed by the easter egg
 *   data-locale     shop locale (de, en, fr, it, es – everything else falls back to en)
 *
 * No dependencies. Animations only touch transform/opacity and stop while the tab is hidden.
 */
(() => {
  'use strict';

  const root = document.getElementById('hw-root');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = '1';

  /* --------------------------------------------------------------------------------------------
   * Config
   * ------------------------------------------------------------------------------------------ */

  const I18N = {
    de: { light: 'Kürbis anzünden', out: 'Kürbis auspusten', hint: 'Licht aus. Irgendwo hier spukt etwas … such es mit der Taschenlampe.', tip: 'Psst … siehst du die Augen?', happy: 'Happy Halloween', codeLabel: 'Dein Code', copy: 'Kopieren', copied: 'Kopiert!', close: 'Licht wieder an', noCode: 'Danke, dass du bei uns vorbeispukst.' },
    en: { light: 'Light the pumpkin', out: 'Blow out the pumpkin', hint: 'Lights out. Something is haunting this page… find it with your flashlight.', tip: 'Psst… see those eyes?', happy: 'Happy Halloween', codeLabel: 'Your code', copy: 'Copy', copied: 'Copied!', close: 'Lights back on', noCode: 'Thanks for haunting by.' },
    fr: { light: 'Allumer la citrouille', out: 'Souffler la citrouille', hint: 'Lumières éteintes. Quelque chose hante cette page… trouve-le avec ta lampe.', tip: 'Psst… tu vois ces yeux ?', happy: 'Joyeux Halloween', codeLabel: 'Ton code', copy: 'Copier', copied: 'Copié !', close: 'Rallumer', noCode: 'Merci pour ta visite hantée.' },
    it: { light: 'Accendi la zucca', out: 'Spegni la zucca', hint: 'Luci spente. Qualcosa infesta questa pagina… trovalo con la torcia.', tip: 'Psst… vedi quegli occhi?', happy: 'Buon Halloween', codeLabel: 'Il tuo codice', copy: 'Copia', copied: 'Copiato!', close: 'Riaccendi la luce', noCode: 'Grazie per la tua visita spettrale.' },
    es: { light: 'Encender la calabaza', out: 'Apagar la calabaza', hint: 'Luces apagadas. Algo ronda esta página… encuéntralo con tu linterna.', tip: 'Psst… ¿ves esos ojos?', happy: 'Feliz Halloween', codeLabel: 'Tu código', copy: 'Copiar', copied: '¡Copiado!', close: 'Encender la luz', noCode: 'Gracias por tu visita espectral.' }
  };

  const LEVELS = {
    subtle: { ghosts: 1, ghostDelay: [20, 40], peek: 0.12, pumpkins: 3, bats: 'scroll' },
    normal: { ghosts: 2, ghostDelay: [7, 16], peek: 0.22, pumpkins: 4, bats: 'session' },
    full: { ghosts: 3, ghostDelay: [3, 8], peek: 0.4, pumpkins: 5, bats: 'repeat' }
  };

  const effects = new Set((root.dataset.effects || '').split(',').map((s) => s.trim()).filter(Boolean));
  const level = LEVELS[root.dataset.intensity] || LEVELS.normal;
  const eggCode = (root.dataset.eggCode || '').trim();
  const lang = (root.dataset.locale || document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
  const t = I18N[lang] || I18N.en;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const small = window.innerWidth < 750;

  /* --------------------------------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------------------------------ */

  const rand = (a, b) => a + Math.random() * (b - a);
  const randInt = (a, b) => Math.floor(rand(a, b + 1));
  const pick = (list) => list[Math.floor(Math.random() * list.length)];
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, k) => a + (b - a) * k;
  const shuffle = (list) => list.map((v) => [Math.random(), v]).sort((a, b) => a[0] - b[0]).map((p) => p[1]);
  const f1 = (n) => Math.round(n * 10) / 10;

  const html = (markup) => {
    const tpl = document.createElement('template');
    tpl.innerHTML = markup.trim();
    return tpl.content.firstElementChild;
  };

  const session = {
    get(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.sessionStorage.setItem(key, value); } catch (e) { /* storage blocked */ } }
  };

  const bezier = (p0, p1, p2, p3, k) => {
    const u = 1 - k;
    return {
      x: u * u * u * p0.x + 3 * u * u * k * p1.x + 3 * u * k * k * p2.x + k * k * k * p3.x,
      y: u * u * u * p0.y + 3 * u * u * k * p1.y + 3 * u * k * k * p2.y + k * k * k * p3.y
    };
  };

  /* One shared animation loop – paused while the tab is hidden. */
  const tickers = new Set();
  let rafId = 0;
  let lastFrame = 0;

  const loop = (now) => {
    const dt = Math.min(0.05, (now - lastFrame) / 1000 || 0);
    lastFrame = now;
    tickers.forEach((fn) => fn(dt, now / 1000));
    rafId = tickers.size ? requestAnimationFrame(loop) : 0;
  };

  const startLoop = () => {
    if (rafId || document.hidden || !tickers.size) return;
    lastFrame = performance.now();
    rafId = requestAnimationFrame(loop);
  };

  const addTicker = (fn) => { tickers.add(fn); startLoop(); };
  const removeTicker = (fn) => tickers.delete(fn);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    } else {
      startLoop();
    }
  });

  /* Shared pointer + scroll state. */
  const pointer = { x: -9999, y: -9999, active: false };

  if (finePointer) {
    window.addEventListener('pointermove', (e) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.active = true;
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', () => { pointer.active = false; });
  }

  const scrollListeners = new Set();
  let lastScrollY = window.scrollY;
  let lastScrollTime = performance.now();

  window.addEventListener('scroll', () => {
    const now = performance.now();
    const dy = window.scrollY - lastScrollY;
    const speed = Math.abs(dy) / Math.max(1, now - lastScrollTime) * 1000;
    lastScrollY = window.scrollY;
    lastScrollTime = now;
    scrollListeners.forEach((fn) => fn(dy, speed));
  }, { passive: true });

  /* Fixed overlay for everything that flies around. */
  const layer = html('<div class="hw-layer" aria-hidden="true"></div>');
  document.body.append(layer);

  /* --------------------------------------------------------------------------------------------
   * Artwork
   * ------------------------------------------------------------------------------------------ */

  const PUMPKIN_PALETTES = {
    orange: ['#ffb46b', '#ef7f2d', '#b4501a'],
    ember: ['#ff9d52', '#d9621f', '#8c3912'],
    bone: ['#fffaf0', '#ece0c9', '#b6a585'],
    ecto: ['#dff1f1', '#8fc0c0', '#55807f']
  };

  const paletteDefs = Object.entries(PUMPKIN_PALETTES).map(([name, [light, mid, dark]]) => `
    <radialGradient id="hw-pk-${name}" cx=".38" cy=".3" r=".85"><stop offset="0" stop-color="${light}"/><stop offset=".55" stop-color="${mid}"/><stop offset="1" stop-color="${dark}"/></radialGradient>
    <radialGradient id="hw-pk-${name}-b" cx=".5" cy=".35" r=".8"><stop offset="0" stop-color="${mid}"/><stop offset="1" stop-color="${dark}"/></radialGradient>
  `).join('');

  document.body.append(html(`
    <svg class="hw-defs" width="0" height="0" aria-hidden="true" focusable="false"><defs>
      <linearGradient id="hw-ghost-body" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#ffffff"/><stop offset=".6" stop-color="#f3fafa"/><stop offset="1" stop-color="#d3e8e8" stop-opacity=".55"/>
      </linearGradient>
      <radialGradient id="hw-flame" cx=".5" cy=".6" r=".65">
        <stop offset="0" stop-color="#fff8d2"/><stop offset=".45" stop-color="#ffc552"/><stop offset="1" stop-color="#f0781a"/>
      </radialGradient>
      ${paletteDefs}
    </defs></svg>
  `));

  /* Ghost: hem waves via SMIL so it works in every browser, eyes blink via CSS. */
  const GHOST_HEM_A = 'M50 6C26 6 12 24 12 50L12 104Q21.5 116 31 104Q40.5 92 50 104Q59.5 116 69 104Q78.5 92 88 104L88 50C88 24 74 6 50 6Z';
  const GHOST_HEM_B = 'M50 6C26 6 12 24 12 50L12 106Q21.5 94 31 106Q40.5 118 50 106Q59.5 94 69 106Q78.5 118 88 106L88 50C88 24 74 6 50 6Z';

  const ghostSVG = (speed = 1.6) => `
    <svg viewBox="0 0 100 124" focusable="false">
      <path class="hw-ghost__arm" d="M14 60C5 60 0 70 4 76C8 72 11 71 14 72Z"><animateTransform attributeName="transform" type="rotate" values="0 14 64;-12 14 64;0 14 64" dur="${speed * 1.3}s" repeatCount="indefinite"/></path>
      <path class="hw-ghost__arm" d="M86 60C95 60 100 70 96 76C92 72 89 71 86 72Z"><animateTransform attributeName="transform" type="rotate" values="0 86 64;12 86 64;0 86 64" dur="${speed * 1.3}s" begin="-.4s" repeatCount="indefinite"/></path>
      <path class="hw-ghost__body" d="${GHOST_HEM_A}"><animate attributeName="d" values="${GHOST_HEM_A};${GHOST_HEM_B};${GHOST_HEM_A}" dur="${speed}s" repeatCount="indefinite" calcMode="spline" keySplines=".45 0 .55 1;.45 0 .55 1"/></path>
      <circle class="hw-ghost__blush" cx="29" cy="62" r="5"/>
      <circle class="hw-ghost__blush" cx="71" cy="62" r="5"/>
      <g class="hw-ghost__eyes">
        <ellipse class="hw-ghost__eye" cx="38" cy="49" rx="5.5" ry="7.5"/>
        <ellipse class="hw-ghost__eye" cx="62" cy="49" rx="5.5" ry="7.5"/>
        <circle class="hw-ghost__shine" cx="40" cy="46" r="1.7"/>
        <circle class="hw-ghost__shine" cx="64" cy="46" r="1.7"/>
      </g>
      <ellipse class="hw-ghost__mouth" cx="50" cy="67" rx="4" ry="4.8"/>
    </svg>`;

  /* Spider hangs head-down: thread attaches at the top of the abdomen (0, 1.5). */
  const SPIDER_SVG = `
    <svg viewBox="-20 0 40 38" focusable="false">
      <g class="hw-spider__legs hw-spider__legs--l">
        <path d="M-3 17Q-12 10-16 3"/><path d="M-3 18.5Q-14 16-18.5 11"/><path d="M-3 20Q-14 23-17.5 28"/><path d="M-3 21.5Q-11 28-13 35"/>
      </g>
      <g class="hw-spider__legs hw-spider__legs--r">
        <path d="M3 17Q12 10 16 3"/><path d="M3 18.5Q14 16 18.5 11"/><path d="M3 20Q14 23 17.5 28"/><path d="M3 21.5Q11 28 13 35"/>
      </g>
      <ellipse class="hw-spider__abdomen" cx="0" cy="10" rx="7" ry="8.5"/>
      <path class="hw-spider__mark" d="M-2.2 6.5H2.2L.6 9.5 2.2 12.5H-2.2L-.6 9.5Z"/>
      <circle class="hw-spider__head" cx="0" cy="21" r="4.4"/>
      <circle class="hw-spider__eye" cx="-1.7" cy="22.6" r="1.05"/>
      <circle class="hw-spider__eye" cx="1.7" cy="22.6" r="1.05"/>
    </svg>`;

  const BAT_SVG = `
    <svg viewBox="0 0 60 30" focusable="false">
      <path class="hw-bat__wing" d="M30 14C24 5 13 3 2 8C7 10 9 13 8 17C12 14 16 16 17 20C20 16 24 17 27 20Z"/>
      <path class="hw-bat__wing" d="M30 14C36 5 47 3 58 8C53 10 51 13 52 17C48 14 44 16 43 20C40 16 36 17 33 20Z"/>
      <path d="M27 11L26.5 6.5 28.8 9.5H31.2L33.5 6.5 33 11C34.5 13 34.5 20 30 23C25.5 20 25.5 13 27 11Z"/>
      <circle class="hw-bat__eye" cx="28.6" cy="12.6" r=".8"/><circle class="hw-bat__eye" cx="31.4" cy="12.6" r=".8"/>
    </svg>`;

  const PUMPKIN_FACES = [
    /* classic: triangle eyes, toothy grin */
    '<path d="M37 51H50L43.5 38Z"/><path d="M70 51H83L76.5 38Z"/><path d="M57 61H63L60 55.5Z"/><path d="M33 67L39.5 71.5 45 67.5 50.5 73.5 55.5 68.5 60 74 64.5 68.5 69.5 73.5 75 67.5 80.5 71.5 87 67C83 82 73 89 60 89S37 82 33 67Z"/>',
    /* sweet: round eyes, soft smile */
    '<ellipse cx="44" cy="49" rx="6.5" ry="7.5"/><ellipse cx="76" cy="49" rx="6.5" ry="7.5"/><path d="M42 68Q60 80 78 68Q72 86 60 86T42 68Z"/>',
    /* sly: slanted eyes, jagged mouth */
    '<path d="M34 42L52 51 37 55Z"/><path d="M86 42L68 51 83 55Z"/><path d="M31 65L39 73 45.5 67 52 75 60 67.5 68 75 74.5 67 81 73 89 65C86 80 75 88 60 88S34 80 31 65Z"/>',
    /* surprised: oval eyes, "oh" mouth */
    '<ellipse cx="45" cy="48" rx="5" ry="8.5"/><ellipse cx="75" cy="48" rx="5" ry="8.5"/><ellipse cx="60" cy="74" rx="7.5" ry="9.5"/>',
    /* wink: one closed eye, lopsided grin with a single tooth */
    '<path d="M38 50Q44 42 51 50Q44 46 38 50Z"/><path d="M70 51H83L76.5 39Z"/><path d="M36 67Q60 92 84 66Q75 74 66 75L64 80H59L58 75Q46 75 36 67Z"/>'
  ];

  const pumpkinSVG = (palette, face) => `
    <svg viewBox="0 0 120 106" focusable="false">
      <ellipse cx="60" cy="101" rx="48" ry="4.5" fill="rgba(27,21,48,.14)"/>
      <path d="M64 15C70 4 82 6 79 13C77 17 71 15 73 12" fill="none" stroke="#6f8045" stroke-width="2" stroke-linecap="round"/>
      <g stroke="rgba(80,30,0,.2)" stroke-width="1.2">
        <ellipse cx="27" cy="62" rx="24" ry="35" fill="url(#hw-pk-${palette}-b)"/>
        <ellipse cx="93" cy="62" rx="24" ry="35" fill="url(#hw-pk-${palette}-b)"/>
        <ellipse cx="43" cy="60" rx="27" ry="40" fill="url(#hw-pk-${palette})"/>
        <ellipse cx="77" cy="60" rx="27" ry="40" fill="url(#hw-pk-${palette})"/>
        <ellipse cx="60" cy="59" rx="25" ry="42" fill="url(#hw-pk-${palette})"/>
      </g>
      <path d="M57 21C56 13 57 8 61 3C63 2 66 3 65 6C62 10 62 15 63 21Z" fill="#5c6b39"/>
      <ellipse cx="50" cy="35" rx="5" ry="10" transform="rotate(-18 50 35)" fill="#fff" opacity=".22"/>
      <g class="hw-pumpkin__face">${PUMPKIN_FACES[face]}</g>
      <g class="hw-pumpkin__lit"><g class="hw-pumpkin__flicker">${PUMPKIN_FACES[face]}</g></g>
    </svg>`;

  /* --------------------------------------------------------------------------------------------
   * Cobwebs (+ spider)
   * ------------------------------------------------------------------------------------------ */

  /**
   * Builds a random orb web spun into a corner, the way real ones are: a taut frame thread is
   * anchored on both edges, spokes run from a hub out to the edges and the frame, and a capture
   * spiral winds outwards. Corner sits at (0, 0); returns the svg and the hub (for the spider).
   */
  const buildWeb = (size, delay, turns) => {
    const a = size * rand(0.82, 1);
    const b = size * rand(0.82, 1);
    const hub = { x: a * rand(0.3, 0.37), y: b * rand(0.3, 0.37) };
    const count = randInt(11, 14);
    const offset = rand(0, Math.PI * 2);

    /* Ray from the hub to the first boundary: top edge (y = 0), side edge (x = 0) or the frame thread. */
    const spokes = Array.from({ length: count }, (_, i) => {
      const angle = offset + (i / count) * Math.PI * 2 + rand(-0.12, 0.12);
      const dx = Math.cos(angle);
      const dy = Math.sin(angle);
      let len = Infinity;
      if (dy < 0) len = Math.min(len, -hub.y / dy);
      if (dx < 0) len = Math.min(len, -hub.x / dx);
      const towardsFrame = dx / a + dy / b;
      if (towardsFrame > 0) len = Math.min(len, (1 - hub.x / a - hub.y / b) / towardsFrame);
      return { dx, dy, len };
    });

    const at = (spoke, k) => ({ x: hub.x + spoke.dx * spoke.len * k, y: hub.y + spoke.dy * spoke.len * k });
    const anim = (d, wait) => `pathLength="1" style="--d:${f1(d)}s;--delay:${f1(delay + wait)}s"`;
    const paths = [];

    paths.push(`<path class="hw-web__frame" ${anim(0.6, 0)} d="M${f1(a)} 0L0 ${f1(b)}"/>`);

    spokes.forEach((s, i) => {
      const end = at(s, 1);
      paths.push(`<path class="hw-web__spoke" ${anim(0.55, 0.25 + i * 0.045)} d="M${f1(hub.x)} ${f1(hub.y)}L${f1(end.x)} ${f1(end.y)}"/>`);
    });

    /* Tiny irregular hub mesh. */
    [0.05, 0.1].forEach((k, j) => {
      const pts = spokes.map((s) => at(s, k * rand(0.85, 1.15)));
      const d = pts.map((p, i) => `${i ? 'L' : 'M'}${f1(p.x)} ${f1(p.y)}`).join('') + 'Z';
      paths.push(`<path class="hw-web__hub" ${anim(0.4, 0.9 + j * 0.15)} d="${d}"/>`);
    });

    /* One continuous capture spiral (free zone around the hub, a few broken segments). */
    const steps = turns * count;
    const spiralPoints = [];
    let d = '';
    let prev = null;

    for (let n = 0; n <= steps; n++) {
      const p = at(spokes[n % count], lerp(0.24, 0.9, n / steps));
      spiralPoints.push(p);

      if (!prev || Math.random() < 0.03) {
        d += `M${f1(p.x)} ${f1(p.y)}`;
      } else {
        /* Capture threads bow slightly towards the hub and sag a little under gravity. */
        const mx = (prev.x + p.x) / 2;
        const my = (prev.y + p.y) / 2;
        d += `Q${f1(mx + (hub.x - mx) * 0.1)} ${f1(my + (hub.y - my) * 0.1 + 1.2)} ${f1(p.x)} ${f1(p.y)}`;
      }
      prev = p;
    }

    paths.push(`<path class="hw-web__spiral" ${anim(2.2, 1.1)} d="${d}"/>`);

    const dew = shuffle(spiralPoints.slice(count)).slice(0, randInt(4, 6)).map((p, i) =>
      `<circle class="hw-web__dew" cx="${f1(p.x)}" cy="${f1(p.y)}" r="${f1(rand(1.1, 1.9))}" style="--delay:${f1(delay + 3 + i * 0.2)}s;--glint:${f1(rand(0, 6))}s"/>`
    ).join('');

    const svg = `
      <svg viewBox="0 0 ${size} ${size}" focusable="false">
        <g class="hw-web__net">${paths.join('')}${dew}</g>
      </svg>`;

    return { svg, hub };
  };

  const initSpider = (host, anchor, mirrored, size) => {
    const el = html(`<div class="hw-spider"><span class="hw-spider__thread"></span><div class="hw-spider__body">${SPIDER_SVG}</div></div>`);
    const x = mirrored ? size - anchor.x : anchor.x;
    el.style.left = `${f1(x)}px`;
    el.style.top = `${f1(anchor.y)}px`;
    host.append(el);

    const thread = el.firstElementChild;
    const body = el.lastElementChild;
    const rest = rand(30, 70) * (small ? 0.75 : 1);

    let len = reduced ? rest : 0;
    let vel = 0;
    let target = 0;
    let stiffness = 30;
    let mode = 'enter';
    let modeUntil = performance.now() / 1000 + 1.8;
    let nextIdle = modeUntil + rand(10, 20);
    let kick = 0;
    let kickVel = 0;

    const render = (swing) => {
      thread.style.transform = `scaleY(${f1(len)})`;
      body.style.transform = `translate3d(-50%, ${f1(len - 1.5)}px, 0)`;
      el.style.transform = `rotate(${swing.toFixed(2)}deg)`;
    };

    if (reduced) {
      render(0);
      return;
    }

    render(0);

    scrollListeners.add((dy) => {
      vel += clamp(dy * 5, -350, 350);
      kickVel += clamp(dy * 0.4, -12, 12);
    });

    const tick = (dt, now) => {
      if (mode === 'enter' && now > modeUntil) {
        mode = 'idle';
        target = rest;
      } else if (mode === 'drop' && now > modeUntil) {
        mode = 'climb';
        stiffness = 30;
        el.classList.add('is-climbing');
      } else if (mode === 'climb') {
        target = Math.max(rest, target - 42 * dt);
        if (target === rest) {
          mode = 'idle';
          el.classList.remove('is-climbing');
          nextIdle = now + rand(10, 22);
        }
      } else if (mode === 'dangle' && now > modeUntil) {
        mode = 'idle';
        target = rest;
        nextIdle = now + rand(10, 22);
      } else if (mode === 'idle' && now > nextIdle) {
        mode = 'dangle';
        target = rest + rand(30, 70);
        modeUntil = now + rand(1.6, 2.6);
      }

      /* Startle: pointer close to the spider makes it drop fast. */
      if (pointer.active && (mode === 'idle' || mode === 'dangle')) {
        const r = body.getBoundingClientRect();
        const dist = Math.hypot(pointer.x - (r.left + r.width / 2), pointer.y - (r.top + r.height / 2));
        if (dist < 70) {
          mode = 'drop';
          stiffness = 80;
          target = len + rand(150, 230);
          modeUntil = now + rand(1.2, 1.8);
          kickVel += rand(-25, 25);
        }
      }

      /* Underdamped spring for the thread length, damped pendulum for the swing. */
      vel += (stiffness * (target - len) - 2 * 0.42 * Math.sqrt(stiffness) * vel) * dt;
      len = clamp(len + vel * dt, 0, rest + 320);
      kickVel += (-9 * kick - 1.6 * kickVel) * dt;
      kick += kickVel * dt;

      render(Math.sin(now * 0.9) * 2.2 + kick);
    };

    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) addTicker(tick); else removeTicker(tick);
    }, { rootMargin: '200px' }).observe(host);
  };

  /**
   * Webs are spun into real corners: the top corners of the content area (the header's bottom edge is
   * the "ceiling") and one corner of the footer, opposite the pumpkins. They scroll with the page.
   */
  const initWebs = () => {
    const rtl = document.documentElement.dir === 'rtl';
    const main = document.getElementById('main') || document.querySelector('main, [role="main"]');
    const footer = document.querySelector('.footer') || document.querySelector('footer');
    const webs = [];

    const spin = (host, corners, size, turns, delay, withSpider) => {
      if (!host) return;
      host.classList.add('hw-web-host');
      const holder = html('<div class="hw-webs" aria-hidden="true"></div>');

      corners.forEach((corner, i) => {
        const { svg, hub } = buildWeb(size, delay + i * 0.4, turns);
        const web = html(`<div class="hw-web hw-web--${corner}" style="--hw-web-size:${size}px">${svg}</div>`);
        holder.append(web);
        webs.push(web);
        if (withSpider && corner === 'tr') initSpider(web, hub, true, size);
      });

      host.prepend(holder);
    };

    spin(main, ['tl', 'tr'], small ? 140 : 240, small ? 5 : 7, 0.3, effects.has('spider'));
    spin(footer, [rtl ? 'tr' : 'tl'], small ? 100 : 160, small ? 5 : 6, 0.3, false);

    /* Brushing over a web makes it quiver – it is anchored, so it trembles instead of drifting. */
    if (!finePointer || reduced) return;

    window.addEventListener('pointermove', () => {
      webs.forEach((web) => {
        if (web.classList.contains('is-trembling')) return;
        const r = web.getBoundingClientRect();
        if (pointer.x < r.left || pointer.x > r.right || pointer.y < r.top || pointer.y > r.bottom) return;
        web.classList.add('is-trembling');
        setTimeout(() => web.classList.remove('is-trembling'), 1000);
      });
    }, { passive: true });
  };

  /* --------------------------------------------------------------------------------------------
   * Ghosts
   * ------------------------------------------------------------------------------------------ */

  const ghosts = [];
  const maxGhosts = small ? 1 : level.ghosts;

  const spawnGhost = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dir = Math.random() < 0.5 ? 1 : -1;
    const scale = rand(0.55, 1.05) * (small ? 0.7 : 1);
    const width = 90 * scale;
    const el = html(`<div class="hw-ghost"><div class="hw-ghost__flip">${ghostSVG(rand(1.3, 2))}</div></div>`);

    el.style.width = `${f1(width)}px`;
    el.style.setProperty('--o', (0.55 + scale * 0.4).toFixed(2));
    el.style.setProperty('--blink', `${f1(rand(3, 6))}s`);
    el.firstElementChild.style.transform = `scaleX(${dir})`;
    layer.append(el);

    ghosts.push({
      el,
      dir,
      width,
      x: dir > 0 ? -width - 20 : vw + 20,
      baseY: rand(0.15, 0.7) * vh,
      speed: rand(45, 85) * (0.5 + scale * 0.6),
      amp: rand(15, 55),
      freq: rand(0.5, 1.1),
      phase: rand(0, Math.PI * 2),
      age: 0,
      pushX: 0,
      pushY: 0,
      offY: 0,
      lastY: null,
      scaredUntil: 0
    });

    addTicker(tickGhosts);
  };

  const tickGhosts = (dt, now) => {
    const vw = window.innerWidth;

    for (let i = ghosts.length - 1; i >= 0; i--) {
      const g = ghosts[i];
      g.age += dt;

      let y = g.baseY + g.offY + g.amp * Math.sin(g.freq * g.age + g.phase) + g.amp * 0.3 * Math.sin(2.3 * g.freq * g.age);
      const cx = g.x + g.width / 2;
      const cy = y + g.width * 0.6;

      /* Flee from the cursor. */
      if (pointer.active) {
        const dx = cx - pointer.x;
        const dy = cy - pointer.y;
        const dist = Math.hypot(dx, dy) || 1;
        const radius = 120 + g.width;

        if (dist < radius) {
          const force = (1 - dist / radius) * 1400;
          g.pushX += (dx / dist) * force * dt;
          g.pushY += (dy / dist) * force * dt;
          if (now > g.scaredUntil) g.el.classList.add('is-scared');
          g.scaredUntil = now + 0.9;
        }
      }

      if (g.scaredUntil && now > g.scaredUntil) {
        g.el.classList.remove('is-scared');
        g.scaredUntil = 0;
      }

      g.pushX *= Math.pow(0.25, dt);
      g.pushY *= Math.pow(0.25, dt);
      g.offY += g.pushY * dt;
      g.offY *= Math.pow(0.85, dt);
      g.x += (g.dir * g.speed + g.pushX) * dt;
      y += g.pushY * dt;

      const vy = g.lastY === null ? 0 : (y - g.lastY) / Math.max(dt, 0.001);
      g.lastY = y;
      const tilt = clamp(vy * 0.08, -12, 12) * g.dir;

      g.el.style.transform = `translate3d(${f1(g.x)}px, ${f1(y)}px, 0) rotate(${tilt.toFixed(1)}deg)`;

      if ((g.dir > 0 && g.x > vw + 60) || (g.dir < 0 && g.x < -g.width - 60)) {
        g.el.remove();
        ghosts.splice(i, 1);
      }
    }

    if (!ghosts.length) removeTicker(tickGhosts);
  };

  const initGhosts = () => {
    scrollListeners.add((dy) => ghosts.forEach((g) => { g.baseY -= dy * 0.15; }));

    const schedule = (first) => {
      const wait = first ? rand(2.5, 4.5) : rand(level.ghostDelay[0], level.ghostDelay[1]);
      setTimeout(() => {
        if (!document.hidden && ghosts.length < maxGhosts) spawnGhost();
        schedule(false);
      }, wait * 1000);
    };

    schedule(true);
  };

  /* --------------------------------------------------------------------------------------------
   * Peeking ghosts on product cards
   * ------------------------------------------------------------------------------------------ */

  const initPeek = () => {
    const haunt = (wrapper) => {
      wrapper.dataset.hw = '1';
      if (Math.random() > level.peek) return;

      const peek = html(`<div class="hw-peek" aria-hidden="true"><div class="hw-peek__ghost">${ghostSVG(1.4)}</div></div>`);
      wrapper.append(peek);
      let hideTimer = 0;
      let showTimer = 0;
      let lastShown = 0;

      const show = () => {
        if (Date.now() - lastShown < 4000) return;
        lastShown = Date.now();
        peek.classList.add('is-peeking');
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => peek.classList.remove('is-peeking'), 2200);
      };

      if (finePointer) {
        wrapper.addEventListener('mouseenter', () => { showTimer = setTimeout(show, 350); });
        wrapper.addEventListener('mouseleave', () => {
          clearTimeout(showTimer);
          clearTimeout(hideTimer);
          peek.classList.remove('is-peeking');
        });
      } else {
        const io = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          if (Math.random() < 0.5) setTimeout(show, rand(400, 1200));
        }, { threshold: 0.9 });
        io.observe(wrapper);
      }
    };

    const scan = () => document.querySelectorAll('.product-item__image-wrapper:not([data-hw])').forEach(haunt);
    scan();

    /* Product grids get re-rendered by filters, recommendations and quick view. */
    let pending = 0;
    new MutationObserver(() => {
      if (pending) return;
      pending = setTimeout(() => { pending = 0; scan(); }, 300);
    }).observe(document.body, { childList: true, subtree: true });
  };

  /* --------------------------------------------------------------------------------------------
   * Ghost flies into the cart after "add to cart"
   * ------------------------------------------------------------------------------------------ */

  const initCartGhost = () => {
    const visible = (node) => {
      const r = node.getBoundingClientRect();
      return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight;
    };

    document.addEventListener('variant:added', (event) => {
      const form = event.target;
      const source = (form.querySelector && form.querySelector('[type="submit"]')) || form;
      if (!source.getBoundingClientRect) return;

      const from = source.getBoundingClientRect();
      const cart = [...document.querySelectorAll('[aria-controls="mini-cart"], .header__icon-wrapper[href$="/cart"]')].find(visible);
      const start = { x: from.left + from.width / 2, y: from.top + from.height / 2 };
      let end = { x: start.x + rand(-40, 40), y: start.y - 220 };

      if (cart) {
        const r = cart.getBoundingClientRect();
        end = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      }

      const ctrl = { x: (start.x + end.x) / 2 + rand(-90, 90), y: Math.max(0, Math.min(start.y, end.y) - rand(100, 180)) };
      const ghost = html(`<div class="hw-cart-ghost" aria-hidden="true">${ghostSVG(0.9)}</div>`);
      document.body.append(ghost);

      const frames = [];
      for (let i = 0; i <= 12; i++) {
        const k = i / 12;
        const u = 1 - k;
        const x = u * u * start.x + 2 * u * k * ctrl.x + k * k * end.x;
        const y = u * u * start.y + 2 * u * k * ctrl.y + k * k * end.y;
        const s = k < 0.15 ? lerp(0.3, 1.1, k / 0.15) : lerp(1.1, 0.35, (k - 0.15) / 0.85);
        frames.push({
          transform: `translate(${f1(x - 20)}px, ${f1(y - 24)}px) rotate(${f1(Math.sin(k * 9) * 10)}deg) scale(${s.toFixed(2)})`,
          opacity: k < 0.06 || k > 0.94 ? 0 : 1
        });
      }

      ghost.animate(frames, { duration: 1150, easing: 'cubic-bezier(.4, 0, .2, 1)' }).onfinish = () => {
        ghost.remove();
        if (!cart) return;
        cart.classList.remove('hw-cart-bump');
        void cart.offsetWidth;
        cart.classList.add('hw-cart-bump');
        setTimeout(() => cart.classList.remove('hw-cart-bump'), 700);
      };
    });
  };

  /* --------------------------------------------------------------------------------------------
   * Pumpkin patch (footer) + lights-out easter egg
   * ------------------------------------------------------------------------------------------ */

  const initPumpkins = () => {
    const footer = document.querySelector('.footer') || document.querySelector('footer');
    if (!footer) return;

    footer.classList.add('hw-footer-host');
    const count = Math.max(2, level.pumpkins - (small ? 1 : 0));
    const patch = html('<div class="hw-patch"></div>');
    const palettes = shuffle(['orange', 'orange', 'ember', 'bone', 'ecto']).slice(0, count);

    /* Keep at least one lit and one dark pumpkin so there is something to click. */
    const litStates = shuffle(Array.from({ length: count }, (_, i) => i === 0 || (i !== 1 && Math.random() < 0.5)));

    const pumpkins = palettes.map((palette, i) => {
      const size = rand(0.65, 1) * (small ? 58 : 84) * (i === Math.floor(count / 2) ? 1.2 : 1);
      let face = randInt(0, PUMPKIN_FACES.length - 1);
      const btn = html(`<button type="button" class="hw-pumpkin"><span class="hw-pumpkin__glow"></span>${pumpkinSVG(palette, face)}</button>`);

      btn.style.setProperty('--s', `${f1(size)}px`);
      btn.style.setProperty('--r', `${f1(rand(-7, 7))}deg`);
      btn.style.setProperty('--fl', `${f1(rand(1.6, 2.8))}s`);
      btn.style.setProperty('--fd', `${f1(-rand(0, 3))}s`);
      btn.style.setProperty('--wd', `${f1(rand(0, 6))}s`);
      btn.style.marginBottom = `${f1(rand(0, 10))}px`;

      const setLit = (lit) => {
        btn.classList.toggle('is-lit', lit);
        btn.setAttribute('aria-label', lit ? t.out : t.light);
        btn.setAttribute('aria-pressed', String(lit));
      };

      setLit(litStates[i]);

      btn.addEventListener('click', () => {
        const lit = !btn.classList.contains('is-lit');
        const svg = btn.querySelector('svg');

        if (lit) {
          face = (face + randInt(1, PUMPKIN_FACES.length - 1)) % PUMPKIN_FACES.length;
          btn.querySelector('.hw-pumpkin__face').innerHTML = PUMPKIN_FACES[face];
          btn.querySelector('.hw-pumpkin__flicker').innerHTML = PUMPKIN_FACES[face];
        } else if (!reduced) {
          puff(btn);
        }

        svg.classList.remove('is-pop');
        void svg.getBoundingClientRect();
        svg.classList.add('is-pop');
        setLit(lit);

        if (lit && pumpkins.every((p) => p.classList.contains('is-lit'))) {
          if (effects.has('egg')) setTimeout(openEgg, 700);
          else celebrate();
        }
      });

      patch.append(btn);
      return btn;
    });

    const celebrate = () => pumpkins.forEach((p, i) => {
      const svg = p.querySelector('svg');
      setTimeout(() => {
        svg.classList.remove('is-hop');
        void svg.getBoundingClientRect();
        svg.classList.add('is-hop');
      }, i * 110);
    });

    if (effects.has('fog')) patch.append(html('<div class="hw-fog hw-fog--patch" aria-hidden="true"><div class="hw-fog__layer"></div><div class="hw-fog__layer"></div></div>'));
    footer.prepend(patch);
  };

  const puff = (btn) => {
    for (let i = 0; i < 3; i++) {
      const smoke = html('<span class="hw-smoke" aria-hidden="true"></span>');
      smoke.style.setProperty('--sx', `${f1(rand(-14, 14))}px`);
      smoke.style.animationDelay = `${i * 0.12}s`;
      btn.append(smoke);
      setTimeout(() => smoke.remove(), 1600);
    }
  };

  const openEgg = () => {
    if (document.querySelector('.hw-egg')) return;

    const returnFocus = document.activeElement;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const codeMarkup = eggCode
      ? `<p class="hw-egg__label">${t.codeLabel}</p>
         <div class="hw-egg__code"><span>${eggCode.replace(/[<>&"]/g, '')}</span><button type="button" class="hw-egg__copy">${t.copy}</button></div>`
      : `<p class="hw-egg__text">${t.noCode}</p>`;

    const egg = html(`
      <div class="hw-egg" role="dialog" aria-modal="true" aria-label="${t.happy}">
        <div class="hw-egg__card">
          <div class="hw-egg__ghost">${ghostSVG(1.4)}</div>
          <p class="hw-egg__title">${t.happy}</p>
          ${codeMarkup}
        </div>
        <div class="hw-egg__eyes"><i></i><i></i></div>
        <div class="hw-egg__dark"></div>
        <div class="hw-egg__beam"></div>
        <div class="hw-egg__dim"></div>
        <p class="hw-egg__hint">${t.hint}</p>
        <button type="button" class="hw-egg__close">${t.close}</button>
      </div>`);

    /* Hide the card fully on screen but away from the centre, where the flashlight starts. */
    const halfW = Math.min(320, vw * 0.78) / 2 + 16;
    const minY = 150;
    const maxY = Math.max(minY, vh - 150);
    let cx;
    let cy;
    let tries = 0;
    do {
      cx = rand(halfW, vw - halfW);
      cy = rand(minY, maxY);
    } while (Math.hypot(cx - vw / 2, cy - vh / 2) < Math.min(vw, vh) * 0.28 && ++tries < 40);

    const card = egg.querySelector('.hw-egg__card');
    const eyes = egg.querySelector('.hw-egg__eyes');
    const dark = egg.querySelector('.hw-egg__dark');
    const beam = egg.querySelector('.hw-egg__beam');
    const hint = egg.querySelector('.hw-egg__hint');
    const closeBtn = egg.querySelector('.hw-egg__close');
    card.style.left = eyes.style.left = `${f1(cx)}px`;
    card.style.top = eyes.style.top = `${f1(cy)}px`;

    const light = { x: vw / 2, y: vh / 2, tx: vw / 2, ty: vh / 2, r: small ? 110 : 150 };
    let found = false;

    const paint = () => {
      [dark, beam].forEach((n) => {
        n.style.setProperty('--x', `${f1(light.x)}px`);
        n.style.setProperty('--y', `${f1(light.y)}px`);
        n.style.setProperty('--r', `${f1(light.r)}px`);
      });
    };

    const onMove = (e) => {
      light.tx = e.clientX;
      light.ty = e.clientY;
    };

    const reveal = () => {
      if (found) return;
      found = true;
      egg.classList.add('is-found');
      const copy = egg.querySelector('.hw-egg__copy');
      (copy || closeBtn).focus();
    };

    const tick = (dt) => {
      const k = reduced ? 1 : Math.min(1, dt * 9);
      light.x += (light.tx - light.x) * k;
      light.y += (light.ty - light.y) * k;
      if (found) light.r += (Math.max(vw, vh) * 1.5 - light.r) * Math.min(1, dt * 2.5);
      paint();
      if (found) return;

      /* The card only materialises inside the beam. */
      const dist = Math.hypot(light.x - cx, light.y - cy);
      card.style.opacity = clamp(1.3 - dist / light.r, 0, 1).toFixed(2);
      if (dist < light.r * 0.55) reveal();
    };

    const close = () => {
      removeTicker(tick);
      egg.removeEventListener('pointermove', onMove);
      document.removeEventListener('keydown', onKey, true);
      egg.classList.remove('is-open');
      setTimeout(() => egg.remove(), 600);
      if (returnFocus && returnFocus.focus) returnFocus.focus();
    };

    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if ((e.key === 'Enter' || e.key === ' ') && !found && document.activeElement !== closeBtn) {
        e.preventDefault();
        reveal();
      }
      if (e.key === 'Tab') {
        /* Keep focus inside the dialog. */
        const focusables = [...egg.querySelectorAll('button')].filter((b) => b.offsetParent !== null);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    egg.addEventListener('pointermove', onMove);
    egg.addEventListener('pointerdown', onMove);
    document.addEventListener('keydown', onKey, true);
    closeBtn.addEventListener('click', close);

    const copyBtn = egg.querySelector('.hw-egg__copy');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const done = () => { copyBtn.textContent = t.copied; };
        if (navigator.clipboard) navigator.clipboard.writeText(eggCode).then(done, done);
        else done();
      });
    }

    /* After a while glowing eyes appear in the dark where the card hides. */
    setTimeout(() => {
      if (found || !egg.isConnected) return;
      egg.classList.add('is-hinting');
      hint.textContent = t.tip;
    }, 7000);

    paint();
    document.body.append(egg);
    requestAnimationFrame(() => egg.classList.add('is-open'));
    closeBtn.focus({ preventScroll: true });
    addTicker(tick);
  };

  /* --------------------------------------------------------------------------------------------
   * Fog, wax drips, bats
   * ------------------------------------------------------------------------------------------ */

  const initFog = () => {
    document.querySelectorAll('.slideshow').forEach((show) => {
      show.append(html('<div class="hw-fog" aria-hidden="true"><div class="hw-fog__layer"></div><div class="hw-fog__layer"></div></div>'));
    });
  };

  const initDrips = () => {
    const bar = document.querySelector('.announcement-bar');
    const host = bar && (bar.closest('.shopify-section') || bar.parentElement);
    if (!host || !bar.offsetHeight) return;

    const color = getComputedStyle(bar).backgroundColor;
    const wrap = html('<div class="hw-drips" aria-hidden="true"></div>');
    host.classList.add('hw-drip-host');
    host.append(wrap);

    /* The section sets "z-index: unset" via its ID selector when not sticky – lift it above the header. */
    if (getComputedStyle(host).zIndex === 'auto') host.style.zIndex = '5';

    let tips = [];

    const draw = () => {
      const width = host.offsetWidth;
      const height = 34;
      let edge = `M0 0V3`;
      let x = 0;
      while (x < width) {
        const step = rand(30, 70);
        edge += `Q${f1(x + step / 2)} ${f1(rand(4, 7))} ${f1(x + step)} 3`;
        x += step;
      }
      edge += `V0Z`;

      tips = [];
      let drips = '';
      for (let dx = rand(20, 80); dx < width - 10; dx += rand(small ? 60 : 70, small ? 140 : 190)) {
        const w = rand(7, 14);
        const len = rand(9, 28);
        const r = w * 0.32;
        const bottom = len - r;
        drips += `<path class="hw-drip" style="--d:${f1(rand(1.8, 4.5))}s;--delay:${f1(rand(0.2, 2.5))}s" d="M${f1(dx - w / 2)} 2C${f1(dx - w * 0.22)} 4 ${f1(dx - r)} ${f1(bottom - r * 2)} ${f1(dx - r)} ${f1(bottom)}A${f1(r)} ${f1(r)} 0 0 0 ${f1(dx + r)} ${f1(bottom)}C${f1(dx + r)} ${f1(bottom - r * 2)} ${f1(dx + w * 0.22)} 4 ${f1(dx + w / 2)} 2Z"/>`;
        tips.push({ x: dx, y: len, r });
      }

      wrap.innerHTML = `<svg viewBox="0 0 ${width} ${height}" style="fill:${color}" focusable="false"><path d="${edge}"/>${drips}</svg>`;
    };

    draw();
    let resizeTimer = 0;
    let lastWidth = host.offsetWidth;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (host.offsetWidth !== lastWidth) { lastWidth = host.offsetWidth; draw(); }
      }, 250);
    });

    if (reduced) return;

    const dropLoop = () => {
      setTimeout(() => {
        if (!document.hidden && tips.length) {
          const tip = pick(tips);
          const svg = wrap.querySelector('svg');
          const drop = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          drop.setAttribute('class', 'hw-drop');
          drop.setAttribute('cx', f1(tip.x));
          drop.setAttribute('cy', f1(tip.y - tip.r * 0.4));
          drop.setAttribute('r', f1(tip.r * 0.9));
          svg.append(drop);
          setTimeout(() => drop.remove(), 1600);
        }
        dropLoop();
      }, rand(3500, 8000));
    };

    dropLoop();
  };

  let batsFlying = false;

  const batSwarm = () => {
    if (batsFlying || document.hidden) return;
    batsFlying = true;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dir = Math.random() < 0.5 ? 1 : -1;
    const y0 = rand(0.3, 0.7) * vh;
    const y1 = y0 + rand(-0.4, 0.15) * vh;
    const count = small ? randInt(4, 6) : randInt(6, 10);
    const startAt = performance.now() / 1000;

    const bats = Array.from({ length: count }, (_, i) => {
      const size = rand(24, 50) * (small ? 0.8 : 1);
      const el = html(`<div class="hw-bat">${BAT_SVG}</div>`);
      el.style.width = `${f1(size)}px`;
      el.style.setProperty('--f', `${rand(0.11, 0.18).toFixed(2)}s`);
      layer.append(el);

      const p0 = { x: dir > 0 ? -80 : vw + 80, y: y0 + rand(-60, 60) };
      const p3 = { x: dir > 0 ? vw + 80 : -80, y: y1 + rand(-80, 80) };
      return {
        el,
        size,
        p0,
        p1: { x: lerp(p0.x, p3.x, 0.33), y: p0.y + rand(-170, 110) },
        p2: { x: lerp(p0.x, p3.x, 0.66), y: p3.y + rand(-110, 170) },
        p3,
        dur: rand(2.6, 4.2),
        delay: i * rand(0.06, 0.16) + rand(0, 0.3),
        wobble: rand(0, Math.PI * 2),
        done: false
      };
    });

    const tick = (dt, now) => {
      let alive = 0;
      bats.forEach((b) => {
        if (b.done) return;
        const elapsed = now - startAt - b.delay;
        const k = elapsed / b.dur;
        if (k < 0) { alive++; return; }
        if (k > 1) { b.el.remove(); b.done = true; return; }
        alive++;
        const p = bezier(b.p0, b.p1, b.p2, b.p3, k);
        const ahead = bezier(b.p0, b.p1, b.p2, b.p3, Math.min(1, k + 0.02));
        const angle = Math.atan2(ahead.y - p.y, Math.abs(ahead.x - p.x)) * 57.3 * 0.35 * dir;
        b.el.style.opacity = '1';
        b.el.style.transform = `translate3d(${f1(p.x - b.size / 2)}px, ${f1(p.y + Math.sin(elapsed * 9 + b.wobble) * 4)}px, 0) rotate(${f1(angle)}deg)`;
      });
      if (!alive) {
        removeTicker(tick);
        batsFlying = false;
      }
    };

    addTicker(tick);
  };

  const initBats = () => {
    let scrollTriggered = false;
    scrollListeners.add((dy, speed) => {
      if (!scrollTriggered && speed > 3200) {
        scrollTriggered = true;
        batSwarm();
      }
    });

    if (level.bats === 'session' && !session.get('hw-bats')) {
      setTimeout(() => { session.set('hw-bats', '1'); batSwarm(); }, rand(30, 70) * 1000);
    } else if (level.bats === 'repeat') {
      const again = () => setTimeout(() => { batSwarm(); again(); }, rand(40, 80) * 1000);
      again();
    }
  };

  /* --------------------------------------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------------------------------------ */

  const boot = () => {
    const run = (name, fn) => {
      if (!effects.has(name)) return;
      try { fn(); } catch (e) { console.warn(`[halloween] ${name}`, e); }
    };

    run('webs', initWebs);
    run('drips', initDrips);
    run('fog', initFog);
    run('pumpkins', initPumpkins);

    if (!reduced) {
      run('ghosts', initGhosts);
      run('peek', initPeek);
      run('cart', initCartGhost);
      run('bats', initBats);
    }

    document.documentElement.classList.add('hw-ready');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
